/**
 * OpenAI Voice Agents Service with Browser Web Speech API
 */

import { OPENAI_CONFIG } from './config';

class OpenAIVoiceService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.recognition = null;
    this.isConnected = false;
    this.isListening = false;
    this.isSpeaking = false;
    this.voices = [];
    this.onStatusChange = null;
    this.onTranscriptUpdate = null;
    this.onAiResponse = null;
    this.onError = null;
    this.onSpeechEnd = null;
    this.recognitionSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    this.speechSupported = 'speechSynthesis' in window;
    this.speechTimeout = null;
    this.retryCount = 0;
    this.maxRetries = 2;
  }

  /**
   * Initialize the voice service
   */
  async initialize() {
    console.log('Initializing AIVA Voice Service...');
    console.log('Speech Recognition Supported:', this.recognitionSupported);
    console.log('Speech Synthesis Supported:', this.speechSupported);

    // Load voices for TTS
    if (this.speechSupported) {
      this.voices = this.synth.getVoices();
      
      if (this.voices.length === 0) {
        await new Promise(resolve => {
          this.synth.onvoiceschanged = resolve;
          setTimeout(resolve, 1000);
        });
        this.voices = this.synth.getVoices();
      }
      
      console.log('Available voices:', this.voices.length);
    }

    // Initialize speech recognition
    if (this.recognitionSupported) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US';

      this.recognition.onstart = () => {
        console.log('Speech recognition started');
        this.isListening = true;
        this.retryCount = 0;
        if (this.onStatusChange) {
          this.onStatusChange('listening');
        }
      };

      this.recognition.onend = () => {
        console.log('Speech recognition ended');
        this.isListening = false;
        if (this.onStatusChange) {
          this.onStatusChange('idle');
        }
      };

      this.recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        this.isListening = false;
        
        // Handle "no-speech" error gracefully - just switch off mic quietly
        if (event.error === 'no-speech') {
          console.log('No speech detected, switching off mic quietly');
          this.retryCount++;
          if (this.retryCount >= this.maxRetries) {
            this.retryCount = 0;
          }
          // Quietly go idle - no error shown
          if (this.onStatusChange) {
            this.onStatusChange('idle');
          }
          return;
        }
        
        if (this.onError) {
          this.onError(event.error);
        }
        if (this.onStatusChange) {
          this.onStatusChange('idle');
        }
      };

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('User said:', transcript);
        this.retryCount = 0;
        
        if (this.onTranscriptUpdate) {
          this.onTranscriptUpdate(transcript, 'user');
        }
      };
    } else {
      console.warn('Speech recognition not supported');
    }

    this.isConnected = true;
    
    if (this.onStatusChange) {
      this.onStatusChange('connected');
    }

    return true;
  }

  /**
   * Start listening for user speech
   */
  startListening() {
    if (!this.recognitionSupported || !this.recognition) {
      console.error('Speech recognition not available');
      if (this.onError) {
        this.onError('Speech recognition not supported');
      }
      return false;
    }

    if (this.isListening) {
      console.log('Already listening');
      return true;
    }

    if (this.isSpeaking) {
      console.log('Currently speaking, cannot start listening');
      return false;
    }

    try {
      if (this.speechTimeout) {
        clearTimeout(this.speechTimeout);
        this.speechTimeout = null;
      }
      
      this.recognition.start();
      console.log('Started listening');
      return true;
    } catch (error) {
      console.error('Error starting recognition:', error);
      if (this.onError) {
        this.onError(error);
      }
      return false;
    }
  }

  /**
   * Stop listening
   */
  stopListening() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  /**
   * Send user text to AI and get response
   */
  async sendToAI(userText) {
    try {
      if (this.onStatusChange) {
        this.onStatusChange('thinking');
      }

      console.log('Sending to AI:', userText);

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: OPENAI_CONFIG.instructions,
            },
            {
              role: 'user',
              content: userText,
            },
          ],
          max_tokens: 150,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('OpenAI API error:', error);
        throw new Error(error.error?.message || 'Failed to get AI response');
      }

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content || "I'm not sure how to help with that.";

      console.log('AI Response:', aiResponse);

      if (this.onAiResponse) {
        this.onAiResponse(aiResponse, 'complete');
      }

      this.speak(aiResponse);

    } catch (error) {
      console.error('Error getting AI response:', error);
      
      if (this.onError) {
        this.onError(error);
      }

      const fallbackResponse = "I'm having trouble connecting to the AI service. Please try again.";
      if (this.onAiResponse) {
        this.onAiResponse(fallbackResponse, 'complete');
      }
      this.speak(fallbackResponse);
    }
  }

  /**
   * Convert text to speech
   */
  speak(text) {
    if (!this.speechSupported) {
      console.warn('Speech synthesis not supported');
      if (this.onSpeechEnd) {
        setTimeout(() => this.onSpeechEnd(), 100);
      }
      return;
    }

    this.synth.cancel();

    // Replace AIVA with phonetic spelling for proper pronunciation
    const processedText = text.replace(/AIVA/g, 'Aiva').replace(/A\.I\.V\.A\./g, 'Aiva');

    const utterance = new SpeechSynthesisUtterance(processedText);
    utterance.rate = 0.85;
    utterance.pitch = 1.1;
    utterance.volume = 1.0;
    utterance.lang = 'en-GB';

    // Log available voices for debugging
    console.log('Available voices:', this.voices.map(v => `${v.name} (${v.lang})`));
    
    // Prioritize consistent voices across devices
    const selectedVoice = 
      // Try Google voices (most consistent across Chrome/Android)
      this.voices.find(voice => voice.name === 'Google UK English Female') ||
      this.voices.find(voice => voice.name === 'Google US English') ||
      // Try Microsoft voices (Windows)
      this.voices.find(voice => voice.name === 'Microsoft Zira Desktop - English (United States)') ||
      this.voices.find(voice => voice.name === 'Microsoft Hazel Desktop - English (Great Britain)') ||
      // Try Apple voices (Mac/iOS)
      this.voices.find(voice => voice.name === 'Samantha') ||
      this.voices.find(voice => voice.name === 'Victoria') ||
      // Generic fallbacks
      this.voices.find(voice => voice.lang === 'en-GB' && voice.name.toLowerCase().includes('female')) ||
      this.voices.find(voice => voice.lang === 'en-US' && voice.name.toLowerCase().includes('female')) ||
      this.voices.find(voice => voice.name.toLowerCase().includes('female')) ||
      // Final fallback - first English voice
      this.voices.find(voice => voice.lang.startsWith('en')) ||
      this.voices[0];
    
    console.log('Selected voice:', selectedVoice?.name, '| Lang:', selectedVoice?.lang);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.onstart = () => {
      console.log('Speaking started');
      this.isSpeaking = true;
      if (this.onStatusChange) {
        this.onStatusChange('speaking');
      }
    };

    utterance.onend = () => {
      console.log('Speaking ended');
      this.isSpeaking = false;
      if (this.onStatusChange) {
        this.onStatusChange('idle');
      }
      this.handleSpeechComplete();
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      this.isSpeaking = false;
      if (this.onStatusChange) {
        this.onStatusChange('idle');
      }
      this.handleSpeechComplete();
    };

    console.log('Speaking:', text.substring(0, 50) + '...');
    this.synth.speak(utterance);
  }

  /**
   * Handle speech completion
   */
  handleSpeechComplete() {
    console.log('Handling speech complete');
    
    if (this.onSpeechEnd) {
      console.log('Calling speech end callback');
      this.onSpeechEnd();
    }
    
    // Start listening after delay
    this.speechTimeout = setTimeout(() => {
      console.log('Auto-starting listening');
      this.speechTimeout = null;
      if (!this.isListening) {
        try {
          this.recognition.start();
        } catch (e) {
          console.error('Error starting recognition:', e);
        }
      }
    }, 1500);
  }

  /**
   * Set the callback to be called when speech ends
   */
  setOnSpeechEnd(callback) {
    this.onSpeechEnd = callback;
  }

  /**
   * Send text input directly
   */
  sendTextInput(text) {
    if (this.onTranscriptUpdate) {
      this.onTranscriptUpdate(text, 'user');
    }
    this.sendToAI(text);
  }

  /**
   * Check if service is ready
   */
  isReady() {
    return this.isConnected;
  }

  /**
   * Check if currently listening
   */
  isCurrentlyListening() {
    return this.isListening;
  }

  /**
   * Check if currently speaking
   */
  isCurrentlySpeaking() {
    return this.isSpeaking;
  }

  /**
   * Disconnect/cleanup
   */
  disconnect() {
    this.stopListening();
    
    if (this.speechSupported) {
      this.synth.cancel();
    }
    
    if (this.speechTimeout) {
      clearTimeout(this.speechTimeout);
      this.speechTimeout = null;
    }

    this.isConnected = false;
    this.isListening = false;
    this.isSpeaking = false;

    console.log('Voice service disconnected');
  }
}

// Export singleton instance
const openAIVoiceService = new OpenAIVoiceService();

export default openAIVoiceService;
